// Imports
import {
  Box,
  Text,
  Flex,
  HStack,
  VStack,
  Select,
  Input,
  Avatar,
  Spinner,
  useDisclosure,
} from "components-lib";
import React, { useState, useEffect, useCallback } from "react";
import ChatService from "services/chatService";
import ChatModal from "components/chat/ChatModal";
import { useChat } from "contexts/ChatContext"; // Import Context

// Configuration constants
// Refresh participants every 5 minutes (300000 ms) to avoid excessive polling
const REFRESH_INTERVAL_MS = 300000; // 5 minutes

/**
 * Doctor Alerts Component
 * Displays doctor messages with filtering capabilities
 */
export default function DoctorAlerts({
  clinicOptions = [],
  doctorOptions = [],
  doctorOptionsRaw = [],
  selectedClinic = 'all',
  onClinicChange = () => { },
  refreshTrigger = 0
}) {
  // State management
  const { chatState, syncMessages, openChat } = useChat(); // Use Context
  const [doctorAlerts, setDoctorAlerts] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [individualUnreadCounts, setIndividualUnreadCounts] = useState({});
  // history UI removed here — we only open chat modal on click

  // Filter states specific to doctor alerts
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');

  // Chat modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedConversation, setSelectedConversation] = useState(null);

  // Ref to track when a conversation was last read locally to persist "seen" state across refetches
  const lastReadTimestamps = React.useRef({});

  // Filter doctors based on selected clinic
  const filteredDoctorOptions = selectedClinic === 'all'
    ? doctorOptions
    : doctorOptionsRaw
      .filter(d => {
        const clinicId = d.clinic_id || d.clinic || d.clinicId;
        return clinicId && `${clinicId}` === `${selectedClinic}`;
      })
      .map(d => ({ id: d.id || d._id, name: d.doctor || d.name }));

  // Fetch doctor alerts from participants API
  const fetchDoctorAlerts = useCallback(async () => {
    setLoadingParticipants(true);
    try {
      // Fetch participants who have messaged admin
      const participants = await ChatService.getAdminParticipants(1, 'admin');

      // Normalize participants into an array. ChatService may return the server body
      // (e.g. { success: true, data: [...] }) or the array directly.
      const participantsArray = Array.isArray(participants)
        ? participants
        : (participants && (participants.data || participants.data?.data)) || [];

      const doctors = [];
      const unreadCounts = {};

      // Build allowed doctor ids when a clinic is selected
      const allowedDoctorIds = selectedClinic && `${selectedClinic}` !== 'all'
        ? doctorOptionsRaw
          .filter(d => {
            const clinicId = d.clinic_id || d.clinic || d.clinicId;
            return clinicId && `${clinicId}` === `${selectedClinic}`;
          })
          .map(d => `${d.id || d._id}`)
        : null;

      if (Array.isArray(participantsArray)) {
        for (const participant of participantsArray) {
          // Only consider doctor-type participants
          if (participant.doctor_id && participant.doctor_name) {
            const participantId = `${participant.doctor_id}`;

            // If clinic filter is active, skip participants not in that clinic
            if (allowedDoctorIds && !allowedDoctorIds.includes(participantId)) {
              continue;
            }

            const participantType = 'doctor';
            const participantName = participant.doctor_name;

            // Single history fetch to derive latest message and unread count
            let latestMessageDate = new Date().toISOString().split('T')[0];
            let rawLatestDate = new Date().toISOString(); // Default to now
            let latestMessage;
            let unreadFromParticipant = 0;

            try {
              const historyResponse = await ChatService.getMessageHistory(
                participantId,
                participantType,
                1,
                'admin'
              );

              const history = Array.isArray(historyResponse) ? historyResponse : (historyResponse.data || []);

              if (Array.isArray(history) && history.length > 0) {
                const sortedHistory = [...history].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                // 1. Latest activity date (from anyone) for sorting
                const latestOverall = sortedHistory[0];
                rawLatestDate = latestOverall.created_at;
                latestMessageDate = new Date(rawLatestDate).toISOString().split('T')[0];

                // 2. Latest message specifically from the DOCTOR for display
                const latestFromDoctor = sortedHistory.find(msg => msg.sender_id == participantId);

                if (latestFromDoctor) {
                  latestMessage = latestFromDoctor.message && latestFromDoctor.message.length > 50
                    ? latestFromDoctor.message.substring(0, 50) + '...'
                    : (latestFromDoctor.message || 'Attachment');
                } else {
                  latestMessage = 'No message from doctor';
                }

                // Filter unread messages: must be unread on server AND newer than our local last read timestamp
                const unreadKey = `${participantType}_${participantId}`;
                const lastReadTime = lastReadTimestamps.current[unreadKey] || 0;

                unreadFromParticipant = history.filter(msg => {
                  const isUnreadServer = !msg.is_read;
                  const msgTime = new Date(msg.created_at).getTime();
                  // Note: Use loose equality for sender_id just in case
                  const isSender = msg.sender_id == participantId;

                  return isSender && isUnreadServer && msgTime > lastReadTime;
                }).length;

                // SYNC TO STORE
                syncMessages(participantId, history);

              }
            } catch (error) {
              console.warn(`Error fetching message history for doctor ${participantName}:`, error);
            }

            // Find doctor's clinic information
            const doctorData = doctorOptionsRaw.find(d => `${d.id || d._id}` === participantId);
            const clinicId = doctorData ? (doctorData.clinic_id || doctorData.clinic || doctorData.clinicId) : null;

            doctors.push({
              id: participant.doctor_id,
              name: participant.doctor_name,
              category: 'Doctor Message',
              message: latestMessage,
              date: latestMessageDate,
              sender_name: participant.doctor_name,
              sender_type: 'doctor',
              sender_id: participant.doctor_id,
              clinic_id: clinicId,
              rawDate: rawLatestDate
            });

            unreadCounts[`${participantType}_${participantId}`] = unreadFromParticipant;
          }
        }
      }


      // Sort doctors by latest activity (newest first)
      doctors.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));

      setDoctorAlerts(doctors);
      setIndividualUnreadCounts(unreadCounts);

    } catch (error) {
      console.error('Error fetching doctor alerts:', error);
      setDoctorAlerts([]);
      setIndividualUnreadCounts({});
    } finally {
      setLoadingParticipants(false);
    }
  }, [selectedClinic]);

  // Fetch data on component mount and when refresh trigger or clinic changes
  useEffect(() => {
    fetchDoctorAlerts();

    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchDoctorAlerts();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [refreshTrigger, fetchDoctorAlerts]);

  // Filter alerts based on selected filters
  const filterAlerts = (alerts) => {
    return alerts.filter(alert => {
      // Clinic filtering
      const clinicMatch = selectedClinic === 'all' ||
        (alert.clinic_id && `${alert.clinic_id}` === `${selectedClinic}`);

      // Date filtering
      const dateMatch = !selectedDate || alert.date === selectedDate;

      return clinicMatch && dateMatch;
    });
  };

  // Process alerts through filters (no sorting or pagination)
  const processedAlerts = filterAlerts(doctorAlerts);

  // Handle message click to open chat
  const handleMessageClick = (message) => {
    // Create participant object for chat modal
    const participant = {
      name: message.sender_name || 'Doctor',
      sender_id: message.sender_id,
      sender_type: message.sender_type,
      type: message.sender_type,
      sender_name: message.sender_name
    };


    // Optimistically mark as read locally so UI updates immediately
    // const unreadKey = `${message.sender_type}_${message.sender_id}`;

    // Update ref to persist this read status
    // lastReadTimestamps.current[unreadKey] = Date.now();

    // setIndividualUnreadCounts(prev => ({
    //   ...prev,
    //   [unreadKey]: 0
    // }));

    // Store handles this now via openChat within Modal, but we can call it here too to be snappy
    openChat(message.sender_id); 

    setSelectedConversation(participant);
    onOpen();
  };

  return (
    <Box bg="white" borderRadius="xl" h="100%" boxShadow="lg" p={4}>
      {/* Header with filters */}
      <VStack spacing={4} align="stretch" mb={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="md" fontWeight="bold">Doctor Alerts</Text>
          <HStack spacing={2}>
            {/* {processedAlerts.length !== doctorAlerts.length && (
              <Box bg="blue.500" color="white" borderRadius="full" px={2} py={1} fontSize="xs">
                {processedAlerts.length} of {doctorAlerts.length}
              </Box>
            )} */}
          </HStack>
        </Flex>

        {/* Filter Controls */}
        <HStack spacing={3} wrap="wrap">
          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Clinic</Text>
            <Select
              value={selectedClinic}
              onChange={(e) => onClinicChange(e.target.value)}
              size="sm"
            >
              <option value="all">All Clinics</option>
              {clinicOptions.map(clinic => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </Select>
          </Box>

          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Doctor</Text>
            <Select
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
              size="sm"
            >
              <option value="all">All Doctors</option>
              {filteredDoctorOptions.map(doctor => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Select>
          </Box>

          <Box minW="150px">
            <Text fontSize="xs" fontWeight="medium" mb={1}>Date</Text>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              size="sm"
            />
          </Box>
        </HStack>
      </VStack>

      {/* Doctors List */}
      <Box maxH="200px" overflowY="auto" mb={4}>
        {loadingParticipants ? (
          <Flex justify="center" align="center" py={8}>
            <Text color="gray.500">Loading doctors...</Text>
          </Flex>
        ) : processedAlerts.length === 0 ? (
          <Flex justify="center" align="center" py={8}>
            <Text color="gray.500">
              {doctorAlerts.length === 0 ? 'No doctors found' : 'No results match your filters'}
            </Text>
          </Flex>
        ) : (
          processedAlerts.map((alert, idx) => {
            const senderId = alert.sender_id;
            const unreadCount = chatState.chats[senderId]?.unseenCount || 0;
            const hasUnread = unreadCount > 0;

            return (
              <Flex
                key={`doctor-${alert.id}`}
                align="center"
                p={3}
                mb={2}
                borderRadius="md"
                bg={hasUnread ? "blue.50" : "transparent"}
                _hover={{ bg: hasUnread ? "blue.100" : "gray.50" }}
                transition="all 0.2s"
                onClick={() => handleMessageClick(alert)}
                style={{ cursor: 'pointer' }}
              >
                <Box position="relative" mr={3}>
                  <Avatar name={alert.name} />
                  {hasUnread && (
                    <Box
                      position="absolute"
                      top="0"
                      right="0"
                      boxSize="3"
                      bg="red.500"
                      borderRadius="full"
                      border="2px solid white"
                    />
                  )}
                </Box>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight={hasUnread ? "bold" : "semibold"}>{alert.name}</Text>
                  <Text fontSize="xs" fontWeight={hasUnread ? "bold" : "normal"} color={hasUnread ? "gray.800" : "gray.500"}>{alert.message}</Text>
                </Box>
                <VStack spacing={1} align="end">
                  {hasUnread && (
                    <Box bg="red.500" color="white" borderRadius="full" px={2} py={1} fontSize="xs">
                      {unreadCount}
                    </Box>
                  )}

                  <Text fontSize="xs" color="gray.400">{alert.date}</Text>
                </VStack>
              </Flex>
            );
          })
        )}
      </Box>


      {/* Chat Modal */}
      <ChatModal
        isOpen={isOpen}
        onClose={onClose}
        participant={selectedConversation ? {
          ...selectedConversation,
          // Ensure correct receiver/sender setup for admin chat
          receiver_id: selectedConversation.sender_id,
          receiver_type: selectedConversation.sender_type
        } : null}
        adminId={1}
        adminType="admin"
      />
    </Box>
  );
}